import type { ColumnMap, CustomField, SortModel } from "@/react/prop-types";
import { ResourceType, EntityTypes } from "@/react/prop-types";
import type {
   AssignmentListServerFilter,
   CoreApiSort,
   WorkDaysObject,
} from "./assignment-list-prop-types";
import { AssignmentSendAction, FieldMappings } from "./assignment-list-prop-types";
import { FindAssignmentsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/enums/find-assignments";
import {
   convertDataTableFilters,
   createRoleAttrs,
   sortOrderMap,
} from "../data-table/data-table-utils";
import type { CellValueChangeParams, DataTableConfig } from "@procore/data-table";
import { FilterFieldType } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";
import { findCustomFieldByIntegrationName } from "@/react/shared/custom-field-utils";
import type { useI18nContext } from "@procore/core-react";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import {
   getTimeOption,
   formatOptions,
   parseAssignmentCustomField,
   setStatusValue,
   formatDateTime,
} from "@/react/shared/helper";
import type { SerializedFindCombinedAssignment } from "@laborchart-modules/lc-core-api/dist/api/assignments/find-combined-assignments";
import {
   handleCustomField,
   setJobValue,
   setPersonGender,
   setPersonValue,
} from "../people-list/helpers";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { formatName } from "@/lib/utils/preferences";
import { DateUtils } from "@/lib/utils/date";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import type { GetAssignmentCreationSupportDataResponse } from "@laborchart-modules/lc-core-api/dist/api/legacy/get-assignment-creation-support-data";

import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import { EquipmentAssignmentStore } from "@/stores/equipment-assignments-store.core";

import { convertDetachedDayToUtcDateWithoutTimezoneOffset } from "@laborchart-modules/common/dist/datetime";

const DATE_FIELDS: any[] = ["start_date", "end_date"];
const NUMERIC_FIELDS: any[] = ["percent_allocated"];
const NESTED_FIELDS_TYPE: any[] = [FilterFieldType.BOOL, FilterFieldType.MULTI_SELECT];
const NUMERIC_FIELDS_TYPE: any[] = [FilterFieldType.NUMBER, FilterFieldType.CURRENCY];

// currently we are mapping the field resource in various places to either person name or person_id
// this is to maintain consistency in the codebase as currently we don't have any filtration or sorting on resource
// resource is basically referring to the person assigned to the assignment or equipment assigned to the assignment
// once we have the filtration and sorting on resource, we can update this mapping

// Filter Name Maps
export const filterNameMaps: { [key: string]: string } = {
   start_date: "Assignment Start Date",
   end_date: "Assignment End Date",
   status: "Assignment Status",
   job_title: "Job Title",
   employee_number: "Employee ID",
   person_status: "Person Status",
   project_status: "Project Status",
   percent_allocated: "Percent Allocation",
   overtime: "Overtime",
   allocation_type: "Allocation Type",
   resource: "Person",
   project: "Project",
   project_roles: "Project Roles",
   project_search: "Project",
   cornerstone_stage: "Project Stage",
};
// Filter Type Maps
export const filterTypeMaps: { [key: string]: FilterFieldType } = {
   start_date: FilterFieldType.DATE,
   end_date: FilterFieldType.DATE,
   status: FilterFieldType.SELECT,
   job_title: FilterFieldType.SELECT,
   employee_number: FilterFieldType.TEXT,
   person_status: FilterFieldType.SELECT,
   project_status: FilterFieldType.SELECT,
   percent_allocated: FilterFieldType.NUMBER,
   overtime: FilterFieldType.BOOL,
   allocation_type: FilterFieldType.SELECT,
   date: FilterFieldType.DATE,
   bool: FilterFieldType.BOOL,
   currency: FilterFieldType.CURRENCY,
   "multi-select": FilterFieldType.MULTI_SELECT,
   number: FilterFieldType.NUMBER,
   select: FilterFieldType.SELECT,
   text: FilterFieldType.TEXT,
   resource: FilterFieldType.SELECT,
   project: FilterFieldType.SELECT,
   project_roles: FilterFieldType.MULTI_SELECT,
   project_search: FilterFieldType.TEXT,
   cornerstone_stage: FilterFieldType.MULTI_SELECT,
};

export const columnHeadersMap: ColumnMap = {
   name: "resource",
   job_title: "position",
   project_name: "project",
   position: "job_title",
   job_number: "job_number",
   start_day: "start_date",
   end_day: "end_date",
   start_time: "start_time",
   end_time: "end_time",
   address_1: "address_1",
   address_2: "address_2",
   city_town: "city_town",
   percent_allocated: "percent_allocated",
   status: "status",
   state_province: "state_province",
   zip_code: "zipcode",
   country: "country",
   gender: "gender",
   label_name: "label_name",
   cost_code_name: "cost_code_name",
   overtime: "overtime",
   work_days: "work_days",
   employee_number: "employee_number",
   custom_fields: "custom_fields",
   project_roles: "Project Roles",
   cornerstone_stage: "cornerstone_stage",
};

export const filterPropertyMaps: { [key: string]: string } = {
   job_title: "person_position",
   status: "status_id",
   percent_allocated: "percent_allocation",
   resource: "person_id",
   project: "project_id",
   project_name: "project_search",
   cornerstone_stage: "cornerstone_stage_id",
};

export const filterFieldMaps: { [key: string]: string } = {
   person_position: "job_title",
   person_id: "resource",
   overtime: "overtime",
   project_status: "project_status",
   project_search: "project_search",
   person_status: "person_status",
   allocation_type: "allocation_type",
   start_date: "start_date",
   end_date: "end_date",
   status_id: "status",
   employee_number: "employee_number",
   project_roles: "project_roles",
   percent_allocation: "percent_allocated",
   cornerstone_stage: "cornerstone_stage",
};

export const convertAssignmentDataTableFilters = (filters: AssignmentListServerFilter[]) =>
   convertDataTableFilters(
      filters,
      {
         nameMapper: filterNameMaps,
         typeMapper: filterTypeMaps,
         propertyMapper: filterPropertyMaps,
      },
      {
         numeric: NUMERIC_FIELDS,
         nestedTypes: NESTED_FIELDS_TYPE,
         numericTypes: NUMERIC_FIELDS_TYPE,
         date: DATE_FIELDS,
      },
   );

export function convertDataTableSort(
   sort: SortModel[],
   isLastNameFirst: boolean = false,
): CoreApiSort {
   sortFieldMaps.resource = isLastNameFirst
      ? FindAssignmentsSortBy.PERSON_NAME_LAST
      : FindAssignmentsSortBy.PERSON_NAME_FIRST;

   //by default it will sort by end date in ascending order as seen in the legacy flow
   if (sort.length == 0)
      return {
         sort_by: FindAssignmentsSortBy.ASSIGNMENT_END_DATE,
         direction: sortOrderMap.asc,
      };
   const sortObj: CoreApiSort = {
      sort_by: sortFieldMaps[sort[0].field],
      direction: sortOrderMap[sort[0].sort],
   };

   /* If the sort field is custom_field, then add the custom_field_id
   fieldId will only be there in case of custom_field, it has been added explicitly to identify the custom_field
   we will also be getting an entity type in this case to switch the custom field entity type for setting the sort_by field
   */
   if (sort[0].fieldId && sort[0].entity) {
      const entity = sort[0].entity;
      sortObj["custom_field_id"] = sort[0].fieldId;
      // by default setting sort_by to assignment_custom_field, will change it based on the entity type
      sortObj["sort_by"] = sortFieldMaps["assignment_custom_field"];
      if (entity === EntityTypes.PEOPLE) {
         sortObj["sort_by"] = sortFieldMaps["person_custom_field"];
      }
      if (entity === EntityTypes.PROJECT) {
         sortObj["sort_by"] = sortFieldMaps["project_custom_field"];
      }
   }
   return sortObj;
}

export const fieldTranslationKeyMap: ColumnMap = {
   project: "views.company.workforce_planning.project",
   resource: "views.company.workforce_planning.people.name",
   job_number: "views.company.workforce_planning.projects.project_number",
   job_title: "views.company.workforce_planning.people.job_title",
   start_date: "views.company.workforce_planning.assignment.start_date",
   end_date: "views.company.workforce_planning.assignment.end_date",
   start_time: "views.company.workforce_planning.assignment.start_time",
   end_time: "views.company.workforce_planning.assignment.end_time",
   percent_allocated: "views.company.workforce_planning.assignment.percent_allocated",
   status: "views.company.workforce_planning.people.status",
   overtime: "views.company.workforce_planning.assignment.overtime",
   cost_code_name: "views.company.workforce_planning.category",
   label_name: "views.company.workforce_planning.subcategory",
   work_days: "views.company.workforce_planning.work_days",
   address_1: "views.company.workforce_planning.projects.address",
   address_2: "views.company.workforce_planning.projects.address_2",
   city_town: "views.company.workforce_planning.projects.city",
   state_province: "views.company.workforce_planning.projects.state",
   zipcode: "views.company.workforce_planning.projects.postal",
   country: "views.company.workforce_planning.projects.country",
   employee_number: "views.company.workforce_planning.people.employee_id",
   gender: "views.company.workforce_planning.people.gender",
   person_status: "views.company.workforce_planning.person_status",
   project_status: "views.company.workforce_planning.project_status",
   allocation_type: "views.company.workforce_planning.allocation_type",
   project_roles: "views.company.workforce_planning.project_details_roles.title",
   cornerstone_stage: "views.company.workforce_planning.assignment.project_stage",
};

export const legacyFieldNamesMap: ColumnMap = {
   project: "project_name",
   resource: "name",
   job_title: "position",
   job_number: "job_number",
   start_date: "start_day",
   end_date: "end_day",
   start_time: "start_time",
   end_time: "end_time",
   percent_allocated: "percent_allocated",
   status: "status",
   address_1: "address_1",
   address_2: "address_2",
   city_town: "city_town",
   state_province: "state_province",
   country: "country",
   zipcode: "zipcode",
   gender: "gender",
   employee_number: "employee_number",
   overtime: "overtime",
   cost_code_name: "cost_code_name",
   label_name: "label_name",
   work_days: "work_days",
   project_status: "project_status",
   person_status: "person_status",
   allocation_type: "allocation_type",
   project_roles: "project_roles",
   status_id: "status_id",
   category_id: "category_id",
   subcategory_id: "subcategory_id",
   cornerstone_stage: "cornerstone_stage_name",
};

export const sortFieldMaps: { [key: string]: FindAssignmentsSortBy } = {
   job_title: FindAssignmentsSortBy.JOB_TITLE,
   project: FindAssignmentsSortBy.PROJECT_NAME,
   job_number: FindAssignmentsSortBy.JOB_NUMBER,
   start_date: FindAssignmentsSortBy.ASSIGNMENT_START_DATE,
   end_date: FindAssignmentsSortBy.ASSIGNMENT_END_DATE,
   status: FindAssignmentsSortBy.STATUS,
   start_time: FindAssignmentsSortBy.ASSIGNMENT_START_TIME,
   end_time: FindAssignmentsSortBy.ASSIGNMENT_END_TIME,
   assignment_custom_field: FindAssignmentsSortBy.ASSIGNMENT_CUSTOM_FIELD,
   person_custom_field: FindAssignmentsSortBy.PERSON_CUSTOM_FIELD,
   project_custom_field: FindAssignmentsSortBy.PROJECT_CUSTOM_FIELD,
   percent_allocated: FindAssignmentsSortBy.ASSIGNMENT_PERCENT_ALLOCATION,
   cost_code_name: FindAssignmentsSortBy.COST_CODE_NAME,
   label_name: FindAssignmentsSortBy.LABEL_NAME,
   resource: FindAssignmentsSortBy.PERSON_NAME_FIRST,
   cornerstone_stage: FindAssignmentsSortBy.PROJECT_STAGE,
};

export const defaultAssignmentListTableConfig: DataTableConfig = {
   columnState: [
      {
         field: "project",
         hidden: false,
         pinned: null,
         width: 280,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "resource",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: 0,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "job_number",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "job_title",
         hidden: false,
         pinned: null,
         width: 180,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "start_date",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "end_date",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "start_time",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "end_time",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "percent_allocated",
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "status",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "overtime",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "cost_code_name", //category
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "label_name", // subcategory
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "work_days",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "address_1",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "address_2",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "city_town",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "state_province",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "zipcode",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "country",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "employee_number",
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "gender",
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "project_status",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "person_status",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "allocation_type",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "project_roles",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "cornerstone_stage",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
   ],
   columnGroupState: [],
   filters: {},
   rowHeight: 49,
   serverFilters: [],
};

export const getFormattedCustomFields = (customFields: CustomField[] | null) => {
   if (!customFields) return [];
   const addEntity = (field: CustomField, entity: EntityTypes) => {
      return {
         ...field,
         entity,
      };
   };

   const customColumns = customFields.reduce<CustomField[]>((acc, field) => {
      const { on_assignments, on_projects, on_people } = field;

      if (on_assignments) {
         acc.push(addEntity(field, EntityTypes.ASSIGNMENT));
      }
      if (on_people) {
         acc.push(addEntity(field, EntityTypes.PEOPLE));
      }
      if (on_projects) {
         acc.push(addEntity(field, EntityTypes.PROJECT));
      }

      return acc;
   }, []);

   return customColumns;
};

export const getResource = (assignmentList: SerializedFindCombinedAssignment) => {
   if (assignmentList.assignment_type === "labor") {
      return setPersonValue(assignmentList.person as any);
   }
   if (assignmentList.assignment_type === "equipment") {
      if (assignmentList.equipment) {
         return {
            name: assignmentList.equipment.identification_number,
            profile_pic_url: null, // equipment team is not pushing profile pic in integration_events
         };
      }
      // Equipment assignment but equipment is missing this might be the case for older assignments when integration_events were not created for equipment
      return {
         name: "",
         profile_pic_url: null,
      };
   }
   return null;
};

export const getJobTitle = (assignmentList: SerializedFindCombinedAssignment) => {
   if (assignmentList.assignment_type === "labor") {
      return setJobValue(assignmentList.person.job_title);
   }
   if (assignmentList.assignment_type === "equipment" && assignmentList.equipment) {
      return { label: assignmentList.equipment.type_name ?? "" };
   }
   return null;
};

export const getEmployeeNumber = (assignmentList: SerializedFindCombinedAssignment) =>
   assignmentList.assignment_type === "labor" ? assignmentList.person.employee_number : null;

export const getGender = (
   assignmentList: SerializedFindCombinedAssignment,
   I18n: ReturnType<typeof useI18nContext>,
) =>
   assignmentList.assignment_type === "labor" && assignmentList.person.gender
      ? setPersonGender(assignmentList.person.gender, I18n)
      : null;

export const getPeopleCustomFields = (assignmentList: SerializedFindCombinedAssignment) =>
   assignmentList.assignment_type === "labor" ? assignmentList.person.custom_fields : null;

//Category and Subcategory in the legacy flow is mapped to cost_code and label respectively
// both category and subcategory are objects with id and name properties
export const getIdAndLabel = (item: any) => {
   return {
      id: item?.id || null,
      label: item?.name || "",
   };
};

export function mapApiAssignmentToTableAssignment(
   assignmentData: SerializedFindCombinedAssignment[],
   I18n: ReturnType<typeof useI18nContext>,
   assignmentCreationSupportData: GetAssignmentCreationSupportDataResponse | null,
) {
   const assignmentListDetails: Array<Record<string, any>> = [];
   for (const assignmentList of assignmentData) {
      const formattedProjectRoles = assignmentList.project.roles.map((role) => ({
         ...role,
         assignee_name: formatName(role.person.name), // reformatting array to pass to the expected arguments to the utility function
      }));
      const categoryOptions = assignmentCreationSupportData?.data
         ? formatOptions(
              assignmentCreationSupportData?.data.grouped_cost_code_options[
                 assignmentList.project.id
              ],
           )
         : [];
      const subCategoryOptions = assignmentCreationSupportData?.data
         ? assignmentCreationSupportData?.data.grouped_label_options
         : [];
      // calling the utility function to generate role attributes
      const roleAttrs: Record<string, any> = createRoleAttrs(formattedProjectRoles);
      // Initialize the base assignment details
      const assignmentDetails: Record<string, any> = {
         ...assignmentList,
         resource: getResource(assignmentList),
         job_title: getJobTitle(assignmentList),
         project: assignmentList.project.name,
         job_number: assignmentList.project.job_number,
         start_date: convertDetachedDayToUtcDateWithoutTimezoneOffset(assignmentList.start_day),
         end_date: convertDetachedDayToUtcDateWithoutTimezoneOffset(assignmentList.end_day),
         start_time: getTimeOption(assignmentList.start_time),
         end_time: getTimeOption(assignmentList.end_time),
         address_1: assignmentList.project.address_1,
         address_2: assignmentList.project.address_2,
         cornerstone_stage: assignmentList.project.cornerstone_stage_id
            ? {
                 id: assignmentList.project.cornerstone_stage_id,
                 name: assignmentList.project.cornerstone_stage_name,
              }
            : null,
         city_town: assignmentList.project.city_town,
         state_province: assignmentList.project.state_province,
         zipcode: assignmentList.project.zipcode,
         country: assignmentList.project.country,
         employee_number: getEmployeeNumber(assignmentList),
         gender: getGender(assignmentList, I18n),
         status: assignmentList.status ? setStatusValue(assignmentList.status) : null,
         project_custom_fields: assignmentList.project.custom_fields,
         people_custom_fields: getPeopleCustomFields(assignmentList),
         cost_code_name: getIdAndLabel(assignmentList.category), // category
         label_name: getIdAndLabel(assignmentList.subcategory), // subcategory
         work_days: DateUtils.getFormattedWorkDays(assignmentList.work_days),
         work_days_object: assignmentList.work_days,
         categoryOptions: categoryOptions,
         subCategoryOptions: subCategoryOptions,
         ...roleAttrs,
      };
      assignmentListDetails.push(assignmentDetails);
   }

   return assignmentListDetails;
}

export const entityTypes = [
   CustomFieldEntity.ASSIGNMENT,
   CustomFieldEntity.PROJECT,
   CustomFieldEntity.PERSON,
];

export const entityTypeMap: Record<string, string> = {
   [EntityTypes.PEOPLE]: "people",
   [EntityTypes.PROJECT]: "projects",
   [EntityTypes.ASSIGNMENT]: "assignments",
   [EntityTypes.REQUEST]: "requests",
};

export const reverseEntityTypeMap: Record<string, string> = {
   people: EntityTypes.PEOPLE,
   projects: EntityTypes.PROJECT,
   assignments: EntityTypes.ASSIGNMENT,
};

export const customFieldFilterEntityTypeMap: Record<string, string> = {
   [EntityTypes.PEOPLE]: "people_custom_fields",
   [EntityTypes.PROJECT]: "project_custom_fields",
   [EntityTypes.ASSIGNMENT]: "assignment_custom_fields",
};

export const customFieldSortEntityTypeMap: Record<string, string> = {
   [EntityTypes.PEOPLE]: sortFieldMaps.person_custom_field,
   [EntityTypes.PROJECT]: sortFieldMaps.project_custom_field,
   [EntityTypes.ASSIGNMENT]: sortFieldMaps.assignment_custom_field,
};

export const mapAssignmentServerFilters = (
   serverFilters: AssignmentListServerFilter[],
   customFields: CustomField[],
) =>
   serverFilters.map((filter) => {
      const { field: parsedField, type: parsedFieldEntityType } = parseAssignmentCustomField(
         filter.field,
      );
      const fieldToFind = parsedField || filter.field;
      const customField = findCustomFieldByIntegrationName(customFields, fieldToFind);
      return customField && parsedFieldEntityType
         ? {
              ...filter,
              fieldId: customField.id,
              fieldType: customField.type,
              entity: parsedFieldEntityType,
           }
         : filter;
   });

const MODAL_CONFIG = {
   [AssignmentSendAction.SendImmediately]: "send_alert",
   [AssignmentSendAction.Schedule]: "schedule_alert",
   [AssignmentSendAction.SaveDraft]: "save_draft_alert",
} as const;

export const getModalContent = (
   action: AssignmentSendAction | null,
   I18n: ReturnType<typeof useI18nContext>,
   selectedRowsCount: number = 0,
) => {
   if (!action || !MODAL_CONFIG[action]) {
      return {
         header: "",
         description: "",
         button: "",
         success: "",
         error: "",
      };
   }

   const configKey = MODAL_CONFIG[action];
   const baseKey = `views.company.workforce_planning.assignment.bulk.alerts.${configKey}`;

   return {
      header: I18n.t(`${baseKey}.header`),
      description: I18n.t(`${baseKey}.description`, { total: selectedRowsCount }),
      button: I18n.t(`${baseKey}.button_text`),
      success: I18n.t(`${baseKey}.success`, { total: selectedRowsCount }),
      error: I18n.t(`${baseKey}.error`),
   };
};

export const getAlertContext = (selectedAction: AssignmentSendAction | null): AlertContext => {
   switch (selectedAction) {
      case AssignmentSendAction.SendImmediately:
         return AlertContext.OPEN;
      case AssignmentSendAction.Schedule:
         return AlertContext.SCHEDULED;
      case AssignmentSendAction.SaveDraft:
         return AlertContext.DRAFTS;
      default:
         return AlertContext.OPEN;
   }
};

export const updateAssignment = async (
   params: CellValueChangeParams<any>,
   customFields: CustomField[] | null,
) => {
   const { id, assignment_type } = params.rowData;
   const { field: fieldName, newValue } = params;

   // Build payload based on field type and custom field properties
   const fieldPayload = buildFieldPayload(fieldName, newValue, customFields);

   const payload = fieldPayload.map((item) => ({ id, ...item }));

   try {
      switch (assignment_type) {
         case ResourceType.EQUIPMENT:
            await EquipmentAssignmentStore.updateSingleEquipmentAssignment({
               equipmentAssignmentId: id,
               update: payload[0],
            }).payload;
            break;

         case ResourceType.LABOR:
            await Assignment2Store.updateAssignmentsStream(payload).stream;
            return true;

         default:
            throw new Error(`Unsupported assignment type: ${assignment_type}`);
      }
   } catch (e: any) {
      reportError(e as NotifiableError, "Helix Assignment List Inline Edit", {
         field: { name: fieldName },
         message: { error: JSON.stringify(e.validation?.[0].message) },
      });
   }
};

// Helper to build field-specific parts of the payload
export const buildFieldPayload = (
   fieldName: string,
   newValue: any,
   customFields: CustomField[] | null,
) => {
   if (fieldName === FieldMappings.START_DATE || fieldName === FieldMappings.END_DATE) {
      if (fieldName === FieldMappings.START_DATE) fieldName = legacyFieldNamesMap.start_date;
      if (fieldName === FieldMappings.END_DATE) fieldName = legacyFieldNamesMap.end_date;
      return [{ [fieldName]: newValue ? DateUtils.getDetachedDay(newValue) : null }];
   }

   if (fieldName === FieldMappings.START_TIME || fieldName === FieldMappings.END_TIME) {
      return [{ [fieldName]: newValue?.value ?? null }];
   }

   if (
      fieldName === FieldMappings.STATUS ||
      fieldName === FieldMappings.COST_CODE_NAME ||
      fieldName === FieldMappings.LABEL_NAME
   ) {
      if (fieldName === FieldMappings.STATUS) fieldName = legacyFieldNamesMap.status_id;

      if (fieldName === FieldMappings.LABEL_NAME) fieldName = legacyFieldNamesMap.subcategory_id;

      if (fieldName === FieldMappings.COST_CODE_NAME) {
         fieldName = legacyFieldNamesMap.category_id;

         return [
            { [fieldName]: newValue?.id ?? null },
            { [legacyFieldNamesMap.subcategory_id]: null },
         ];
      }
      return [{ [fieldName]: newValue?.id ?? null }];
   }
   if (fieldName === FieldMappings.PERCENT_ALLOCATED) {
      const percent = Number(newValue);

      return [{ [fieldName]: percent }];
   }

   const customFieldName = fieldName.replace(`${EntityTypes.ASSIGNMENT}'s `, "");

   const customFieldPayload = handleCustomField(customFieldName, newValue, customFields);
   return customFieldPayload
      ? [{ custom_fields: customFieldPayload }]
      : [{ [customFieldName]: newValue }];
};

export const getSelectedDay = (date: Date, workDaysObject: WorkDaysObject): boolean | null => {
   if (!date || !workDaysObject) {
      return null;
   }

   // Validate the date
   const validDate =
      date instanceof Date && !isNaN(date.getTime())
         ? new Date(
              formatDateTime(date, {
                 locale: "en", // Using 'en' to ensure consistent parsing
                 isRequiredTimezone: true,
                 timeZone: "UTC",
              }),
           )
         : null;

   if (!validDate) {
      return null;
   }

   // Get the day of the week
   const day = validDate.getDay().toString() as keyof WorkDaysObject;

   const isWorkDay = workDaysObject[day];

   if (isWorkDay === undefined) {
      return null;
   }

   return isWorkDay;
};
